import type { TFunction } from "i18next";

import { ApiError } from "@/api/client";

const knownCodes = [
  "forbidden",
  "not_found",
  "email_taken",
  "team_name_taken",
  "last_admin",
  "cannot_modify_self",
  "user_not_invited",
  "user_not_active",
  "invalid_token",
  "too_many_attempts",
  "invalid_mfa_code",
  "invalid_credentials",
  "session_expired",
  "mfa_required_by_policy",
  "mfa_already_enabled",
  "mfa_setup_required",
  "setup_completed",
  "invalid_setup_token",
  "project_archived",
  "project_has_time",
  "device_auth_closed",
  "already_exists",
  "manual_time_disabled",
  "overlaps_tracked",
  "overlaps_manual",
  "not_pending",
  "not_cancellable",
  "too_many_exports",
  "export_not_ready",
] as const;

const knownFieldCodes = [
  "too_short",
  "too_long",
  "same_as_email",
  "invalid",
  "length",
  "required",
  "expired",
  "enable_own_mfa_first",
  "manager_role_required",
  "unknown_team",
  "unknown_user",
  "not_member",
  "cannot_remove_self",
  "invalid_domain",
  "before_from",
  "period_too_long",
] as const;

/** Messages that depend on the field as well as the code. */
const specificFieldCodes = {
  "ended_at:too_long": "spanTooLong",
  "ended_at:too_short": "spanTooShort",
  "ended_at:in_future": "inFuture",
  "ended_at:before_start": "beforeStart",
  "started_at:too_old": "tooOld",
  "project_id:not_allowed": "projectNotAllowed",
  "task_id:not_in_project": "taskNotInProject",
  "user_id:not_active": "userNotActive",
  "until:in_past": "untilInPast",
  "until:before_from": "untilBeforeFrom",
  "until:too_long": "vacationTooLong",
  "from:out_of_range": "fromOutOfRange",
  "version:exists": "versionExists",
  "arch:universal_macos_only": "universalMacOnly",
  "group_by:required": "groupRequired",
} as const;

type KnownCode = (typeof knownCodes)[number];
type KnownFieldCode = (typeof knownFieldCodes)[number];

/** Human-readable text for an API error. */
export function errorText(t: TFunction, err: unknown): string {
  if (err instanceof ApiError) {
    if ((knownCodes as readonly string[]).includes(err.code)) return t(`errors.${err.code as KnownCode}`);
    if (err.code === "validation_failed") {
      const [field, code] = Object.entries(err.fields)[0] ?? [];
      const specific = specificText(t, field, code);
      if (specific) return specific;
      if (code && (knownFieldCodes as readonly string[]).includes(code)) {
        return t(`fieldErrors.${code as KnownFieldCode}`);
      }
      return t("errors.validation");
    }
    return t("errors.generic");
  }
  return t("errors.network");
}

function specificText(t: TFunction, field?: string, code?: string): string | undefined {
  const key = `${field}:${code}`;
  if (!(key in specificFieldCodes)) return undefined;
  return t(`fieldErrors.specific.${specificFieldCodes[key as keyof typeof specificFieldCodes]}`);
}

/** Text for a validation error of one field, if the error is about it. */
export function fieldError(t: TFunction, err: unknown, field: string): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const code = err.fields[field];
  if (!code) return undefined;
  const specific = specificText(t, field, code);
  if (specific) return specific;
  return (knownFieldCodes as readonly string[]).includes(code)
    ? t(`fieldErrors.${code as KnownFieldCode}`)
    : t("fieldErrors.invalid");
}
